import { useCallback, useEffect, useRef } from 'react'
import { type LakeDocument, lakeApi } from '@/services/api/lake'

const POLLING_INTERVAL = 3000

/**
 * Returns true if the document has any active (non-terminal) processing state
 * that warrants continued polling.
 *
 * Driven by the single lake indexing lifecycle (`indexing_status`) plus the
 * analysis probe. `pending` is treated as active so polling bridges the gap
 * between analysis completing and the worker flipping `indexing_status` to
 * `processing` — terminal states are `indexed`, `failed`, and `skipped`.
 */
function isDocumentActive(doc: LakeDocument): boolean {
  if (doc.analysis_status === 'analyzing') return true
  return doc.indexing_status === 'pending' || doc.indexing_status === 'processing'
}

interface UseLakeDocumentStatusOptions {
  document: LakeDocument
  onDocumentUpdate?: (doc: LakeDocument) => void
}

/**
 * Polls the document status endpoint while the document is actively processing.
 * - Pauses when the browser tab is hidden (Page Visibility API).
 * - Self-terminates when the document reaches a terminal state.
 * - Uses a fixed 3s interval with no immediate tick on start to avoid
 *   showing a transient 'scheduled' state right after an optimistic update.
 */
export function useLakeDocumentStatus({
  document,
  onDocumentUpdate,
}: UseLakeDocumentStatusOptions): void {
  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const mountedRef = useRef(true)
  // Keep a stable ref to the latest callback so the interval closure stays fresh
  const onDocumentUpdateRef = useRef(onDocumentUpdate)
  onDocumentUpdateRef.current = onDocumentUpdate

  const stopPolling = useCallback(() => {
    if (pollingRef.current !== null) {
      clearInterval(pollingRef.current)
      pollingRef.current = null
    }
  }, [])

  const fetchDocument = useCallback(
    async (documentId: string) => {
      if (window.document.hidden) return
      try {
        const updated = await lakeApi.getLakeDocument(documentId)
        if (!mountedRef.current) return
        onDocumentUpdateRef.current?.(updated)
        if (!isDocumentActive(updated)) {
          stopPolling()
        }
      } catch {
        // Silently ignore transient network errors — polling will retry next tick
      }
    },
    [stopPolling]
  )

  // Start / stop polling based on whether the document is active
  useEffect(() => {
    mountedRef.current = true

    if (!isDocumentActive(document)) {
      stopPolling()
      return
    }

    // Don't double-start if already polling
    if (pollingRef.current !== null) return

    pollingRef.current = setInterval(() => {
      fetchDocument(document.id)
    }, POLLING_INTERVAL)

    return () => {
      // Cleanup on unmount or when document.id changes
      stopPolling()
      mountedRef.current = false
    }
    // Re-evaluate whenever the document identity or active state changes.
    // Using document.id + a derived boolean keeps the dep array stable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [document.id, fetchDocument, stopPolling, document])

  // Pause polling when the tab is hidden; resume when visible again
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!window.document.hidden && isDocumentActive(document) && pollingRef.current === null) {
        pollingRef.current = setInterval(() => {
          fetchDocument(document.id)
        }, POLLING_INTERVAL)
      }
    }

    window.document.addEventListener('visibilitychange', handleVisibilityChange)
    return () => {
      window.document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [document, fetchDocument])

  // Final cleanup on unmount
  useEffect(() => {
    return () => {
      mountedRef.current = false
      stopPolling()
    }
  }, [stopPolling])
}
